import React, { HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import styles from './SubHeading.module.css';
import {
  foregroundColorVariants,
  type ForegroundVariants,
} from '../../utils/foregroundColour/foregroundColor';
import {
  generateResponsiveClasses,
  getBaseValue,
  WithResponsiveProps,
} from '../../utils/breakpoint/responsiveSSR';

const getResponsiveClasses = generateResponsiveClasses(styles);

const subHeading = cva(styles.subHeading, {
  variants: {
    size: {
      xs: styles['size--xs'],
      sm: styles['size--sm'],
    },
    textAlign: {
      start: styles['align--start'],
      center: styles['align--center'],
      end: styles['align--end'],
    },
    truncate: {
      true: styles['truncate--true'],
      false: styles['truncate--false'],
    },
  },
  defaultVariants: {
    size: 'sm',
    textAlign: 'start',
  },
});

export type SubHeadingVariants = VariantProps<typeof subHeading>;
type SubHeadingLevel = 'span' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
type SubHeadingSize = 'xs' | 'sm';

export interface BaseProps
  extends SubHeadingVariants,
    HTMLAttributes<HTMLHeadingElement | HTMLSpanElement> {
  /**
   * children
   */
  children: React.ReactNode;

  /**
   * The heading element to render
   */
  as?: SubHeadingLevel;

  /**
   * The size of the heading
   */
  size?: SubHeadingSize;

  /**
   * The foreground color of the heading
   * @default "accentPrimary"
   */
  foregroundColor?: ForegroundVariants['foregroundColor'];

  /**
   * The text alignment of the heading
   * @default "start"
   */
  textAlign?: 'start' | 'center' | 'end';

  /**
   * **[Responsive]** Truncate text with ellipsis
   * @default false
   */
  truncate?: boolean;
}

// ResponsivePropKeys are the keys of the props that can accept responsive values (base + breakpoint-specific)
type ResponsivePropKeys = 'size' | 'textAlign' | 'truncate';

export type SubHeadingProps = WithResponsiveProps<
  BaseProps,
  ResponsivePropKeys
>;

export const SubHeading = ({
  children,
  as: Component = 'span',
  size,
  foregroundColor = 'accentPrimary',
  textAlign = 'start',
  truncate = false,
  ...props
}: SubHeadingProps) => {
  // Get the base values for size and textAlign to apply the correct default variants
  const baseSize = getBaseValue(size, 'sm');
  const baseTextAlign = getBaseValue(textAlign, 'start');
  const truncateBase = getBaseValue(truncate, false);

  // Generate responsive classes for size and textAlign
  const sizeResp = getResponsiveClasses(size, 'size');
  const alignResp = getResponsiveClasses(textAlign, 'align');
  const truncateResp = getResponsiveClasses(truncate, 'truncate');
  return (
    <Component
      className={[
        subHeading({
          size: baseSize,
          textAlign: baseTextAlign,
          truncate: truncateBase,
        }),
        foregroundColorVariants({ foregroundColor }),
        ...sizeResp,
        ...alignResp,
        ...truncateResp,
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    >
      {children}
    </Component>
  );
};

export default SubHeading;
